/**
 * @fileoverview AI-денойзинг и апскейлинг на compute-шейдерах
 * @module MikhmelViewer/Cinematic10/AIRendering
 *
 * Два класса из роадмапа v10:
 *
 *  AIDenoiser — подавление шума, порождаемого стохастическими эффектами
 *  (SSAO, SSGI, volumetric). Работает в двух режимах: быстрый direct
 *  (анизотропный билатеральный фильтр на compute) и neural (ONNX-модель,
 *  если она загружена и укладывается в бюджет кадра).
 *
 *  Upscaler — рендеринг в пониженном разрешении с последующим подъёмом.
 *  Реализован FSR 1.0 (EASU + RCAS) — открытый алгоритм AMD, работает на
 *  любой видеокарте.
 *
 * О DLSS. Публичного веб-API у DLSS нет: это закрытая технология NVIDIA,
 * доступная через нативные SDK (Vulkan/DX12) и требующая тензорных ядер.
 * Из браузера её вызвать нельзя ни при каких условиях. Режим 'dlss' здесь
 * оставлен для совместимости с интерфейсом и молча переключается на FSR,
 * о чём сообщается через getActiveMode().
 */

import { COMPUTE_PASSES } from './wgslCompute.js';
import { FORMATS } from './webgpuBackend.js';
import { ensureOrt, getExecutionProviders } from './ortLoader.js';

const Ok = (value) => ({ success: true, value });
const Err = (error) => ({ success: false, error });

let _log = { debug: () => {}, info: () => {}, warn: () => {}, error: () => {} };
export function setAILogger(logger) { if (logger) _log = logger; }

// ═══════════════════════════════════════════════════════════════════
// AI DENOISER
// ═══════════════════════════════════════════════════════════════════

export const DENOISE_PRESETS = Object.freeze({
  off:      { strength: 0.0, radius: 1, depthSigma: 60, colorSigma: 20, spatialSigma: 1.0, anisotropic: false },
  light:    { strength: 0.4, radius: 1, depthSigma: 80, colorSigma: 25, spatialSigma: 1.0, anisotropic: true },
  balanced: { strength: 0.7, radius: 2, depthSigma: 60, colorSigma: 15, spatialSigma: 1.4, anisotropic: true },
  strong:   { strength: 1.0, radius: 2, depthSigma: 40, colorSigma: 8,  spatialSigma: 1.8, anisotropic: true },
});

export class AIDenoiser {
  /**
   * @param {import('./webgpuBackend.js').WebGPUBackend} backend
   * @param {object} options
   */
  constructor(backend, options = {}) {
    this._backend = backend;
    this._preset = options.preset || 'balanced';
    this._params = { ...DENOISE_PRESETS[this._preset] };
    this._neuralModel = null;
    this._neuralEnabled = false;
    this._modelPath = options.modelPath || null;
    this._lastMode = 'direct';
    this._disposed = false;

    // Бюджет: если денойз занимает больше этой доли кадра, он деградирует
    this._budgetMs = options.budgetMs ?? 3.0;
    this._lastCostMs = 0;
  }

  setPreset(name) {
    const preset = DENOISE_PRESETS[name];
    if (!preset) return Err(`Unknown denoise preset: ${name}`);
    this._preset = name;
    this._params = { ...preset };
    return Ok(true);
  }

  setParams(partial) {
    this._params = { ...this._params, ...partial };
    return Ok(true);
  }

  getParams() { return { ...this._params }; }
  getActiveMode() { return this._lastMode; }
  getCost() { return this._lastCostMs; }

  /**
   * Загрузка нейросетевой модели денойзинга (ONNX Runtime Web).
   * Модель ожидается в формате: вход [1,4,H,W] (RGB + depth), выход [1,3,H,W].
   *
   * Честное предупреждение: инференс полноразмерной U-Net на каждый кадр
   * в браузере не укладывается в 16 мс даже на WebGPU-бэкенде ORT.
   * Практический сценарий — применять её к финальному кадру при экспорте
   * скриншота или при паузе взаимодействия, а не в реальном времени.
   */
  async loadNeuralModel(path = null) {
    const modelPath = path || this._modelPath;
    if (!modelPath) return Err('No model path configured');

    try {
      const ort = await ensureOrt();
      if (!ort) return Err('ONNX Runtime Web недоступен');

      const providers = getExecutionProviders();
      this._neuralModel = await ort.InferenceSession.create(modelPath, {
        executionProviders: providers,
        graphOptimizationLevel: 'all',
      });

      _log.info(`[AIDenoiser] Модель загружена: ${modelPath} (провайдеры: ${providers.join(', ')})`);
      return Ok(true);
    } catch (e) {
      _log.warn('[AIDenoiser] Загрузка модели не удалась, остаёмся на direct-режиме:', e.message);
      this._neuralModel = null;
      return Err(e.message);
    }
  }

  setNeuralEnabled(enabled) {
    if (enabled && !this._neuralModel) {
      return Err('Neural model not loaded — call loadNeuralModel() first');
    }
    this._neuralEnabled = !!enabled;
    return Ok(true);
  }

  hasNeuralModel() { return !!this._neuralModel; }

  /**
   * Денойзинг внутри уже открытого кадра бэкенда.
   *
   * @param {object} args
   *   srcView   — GPUTextureView с шумным изображением
   *   depthView — GPUTextureView глубины (или null)
   *   dstName   — имя целевого storage-таргета
   *   width, height
   *   hasDepth
   * @returns {import('./webgpuBackend.js').RenderTarget|null}
   */
  denoise({ srcView, depthView, dstName, width, height, hasDepth }) {
    if (this._disposed || !this._backend?.isReady()) return null;
    if (this._params.strength < 0.001) return null;

    const start = performance.now();
    const dst = this._backend.ensureTarget(dstName, width, height, FORMATS.HDR, true);

    this._backend.runComputePass('denoise', {
      resources: {
        texSrc: srcView,
        texDepth: depthView || srcView,
        texDst: dst.view,
      },
      uniforms: {
        uResolution: [width, height],
        uDepthSigma: this._params.depthSigma,
        uColorSigma: this._params.colorSigma,
        uSpatialSigma: this._params.spatialSigma,
        uRadius: this._params.radius,
        uHasDepth: hasDepth ? 1 : 0,
        uAnisotropic: this._params.anisotropic ? 1 : 0,
        uStrength: this._params.strength,
        uFrame: this._backend.getFrameIndex(),
      },
      dispatch: [width, height, 1],
    });

    this._lastMode = 'direct';
    this._lastCostMs = performance.now() - start;

    // Автодеградация: если проход стабильно вылезает за бюджет,
    // уменьшаем радиус, вместо того чтобы ронять частоту кадров
    if (this._lastCostMs > this._budgetMs && this._params.radius > 1) {
      this._params.radius = 1;
      _log.debug('[AIDenoiser] Бюджет превышен, радиус снижен до 1');
    }

    return dst;
  }

  /**
   * Нейросетевой денойзинг одного кадра (вне цикла реального времени).
   * @param {Float32Array} rgbaData данные кадра, RGBA float, длина w*h*4
   */
  async denoiseNeural(rgbaData, width, height) {
    if (!this._neuralModel || !this._neuralEnabled) {
      return Err('Neural denoising not available');
    }

    try {
      const ort = await ensureOrt();
      if (!ort) return Err('ONNX Runtime Web недоступен');

      // NCHW: модель ждёт планарный формат, а кадр приходит интерливленным
      const planar = new Float32Array(3 * width * height);
      const plane = width * height;
      for (let i = 0; i < plane; i++) {
        planar[i] = rgbaData[i * 4];
        planar[plane + i] = rgbaData[i * 4 + 1];
        planar[plane * 2 + i] = rgbaData[i * 4 + 2];
      }

      const input = new ort.Tensor('float32', planar, [1, 3, height, width]);
      const results = await this._neuralModel.run({ input });
      const outKey = Object.keys(results)[0];
      const out = results[outKey].data;

      // Обратно в интерливленный RGBA
      const rgba = new Float32Array(plane * 4);
      for (let i = 0; i < plane; i++) {
        rgba[i * 4] = out[i];
        rgba[i * 4 + 1] = out[plane + i];
        rgba[i * 4 + 2] = out[plane * 2 + i];
        rgba[i * 4 + 3] = 1.0;
      }

      this._lastMode = 'neural';
      return Ok(rgba);
    } catch (e) {
      _log.error('[AIDenoiser] Нейросетевой инференс не удался:', e.message);
      return Err(e.message);
    }
  }

  dispose() {
    this._disposed = true;
    try { this._neuralModel?.release?.(); } catch { /* нечего освобождать */ }
    this._neuralModel = null;
  }
}

// ═══════════════════════════════════════════════════════════════════
// UPSCALER — FSR 1.0
// ═══════════════════════════════════════════════════════════════════

/** Коэффициенты качества FSR: во сколько раз падает разрешение рендера. */
export const UPSCALE_QUALITY = Object.freeze({
  native:          { scale: 1.0,    sharpness: 0.0, label: 'Нативное' },
  ultraQuality:    { scale: 1.3,    sharpness: 0.2, label: 'Ультра качество' },
  quality:         { scale: 1.5,    sharpness: 0.25, label: 'Качество' },
  balanced:        { scale: 1.7,    sharpness: 0.3, label: 'Баланс' },
  performance:     { scale: 2.0,    sharpness: 0.35, label: 'Производительность' },
  ultraPerformance:{ scale: 3.0,    sharpness: 0.45, label: 'Ультра производительность' },
});

export class Upscaler {
  constructor(backend, options = {}) {
    this._backend = backend;
    this._mode = 'fsr';
    this._quality = options.quality || 'quality';
    this._sharpness = options.sharpness ?? UPSCALE_QUALITY[this._quality].sharpness;
    this._rcasEnabled = options.rcas !== false;
    this._rcasDenoise = options.rcasDenoise ?? false;
    this._disposed = false;
    this._warnedDlss = false;
  }

  /**
   * @param {'fsr'|'dlss'|'nis'|'off'} mode
   */
  setMode(mode) {
    if (mode === 'dlss') {
      if (!this._warnedDlss) {
        _log.warn('[Upscaler] DLSS недоступен из браузера (закрытый нативный SDK NVIDIA). Используется FSR 1.0.');
        this._warnedDlss = true;
      }
      this._mode = 'fsr';
      return Ok('fsr');
    }
    if (!['fsr', 'nis', 'off'].includes(mode)) return Err(`Unknown upscale mode: ${mode}`);
    this._mode = mode;
    return Ok(mode);
  }

  setQuality(quality) {
    const q = UPSCALE_QUALITY[quality];
    if (!q) return Err(`Unknown quality level: ${quality}`);
    this._quality = quality;
    this._sharpness = q.sharpness;
    return Ok(true);
  }

  setSharpness(value) {
    this._sharpness = Math.max(0, Math.min(1, Number(value) || 0));
    return Ok(true);
  }

  getActiveMode() { return this._mode; }
  getQuality() { return this._quality; }
  getScale() { return UPSCALE_QUALITY[this._quality]?.scale || 1.0; }
  isActive() { return this._mode !== 'off' && this.getScale() > 1.001; }

  /**
   * Разрешение, в котором нужно рендерить сцену, чтобы после апскейла
   * получить targetW × targetH.
   */
  getRenderResolution(targetW, targetH) {
    const scale = this.getScale();
    if (!this.isActive()) return { width: targetW, height: targetH };
    return {
      width: Math.max(1, Math.round(targetW / scale)),
      height: Math.max(1, Math.round(targetH / scale)),
    };
  }

  /**
   * Апскейл внутри открытого кадра.
   *
   * @returns {import('./webgpuBackend.js').RenderTarget|null} финальный таргет
   */
  upscale({ srcView, srcWidth, srcHeight, targetWidth, targetHeight, prefix = 'upscale' }) {
    if (this._disposed || !this._backend?.isReady()) return null;
    if (!this.isActive()) return null;

    // EASU: собственно подъём разрешения с учётом направления границ
    const easuTarget = this._backend.ensureTarget(
      `${prefix}_easu`, targetWidth, targetHeight, FORMATS.HDR, true
    );

    this._backend.runComputePass('fsrEasu', {
      resources: { texSrc: srcView, texDst: easuTarget.view },
      uniforms: {
        uInputSize: [srcWidth, srcHeight],
        uOutputSize: [targetWidth, targetHeight],
        uSharpness: this._sharpness,
        uPad0: 0,
      },
      dispatch: [targetWidth, targetHeight, 1],
    });

    if (!this._rcasEnabled) return easuTarget;

    // RCAS: возвращает резкость, потерянную при интерполяции.
    // Без него FSR даёт заметно мыльную картинку.
    const rcasTarget = this._backend.ensureTarget(
      `${prefix}_rcas`, targetWidth, targetHeight, FORMATS.HDR, true
    );

    this._backend.runComputePass('fsrRcas', {
      resources: { texSrc: easuTarget.view, texDst: rcasTarget.view },
      uniforms: {
        uResolution: [targetWidth, targetHeight],
        uSharpness: this._sharpness,
        uDenoise: this._rcasDenoise ? 1 : 0,
      },
      dispatch: [targetWidth, targetHeight, 1],
    });

    return rcasTarget;
  }

  /** Только резкость, без изменения разрешения (для нативного режима). */
  sharpenOnly({ srcView, width, height, prefix = 'sharpen' }) {
    if (this._disposed || !this._backend?.isReady()) return null;
    if (this._sharpness < 0.001) return null;

    const target = this._backend.ensureTarget(prefix, width, height, FORMATS.HDR, true);
    this._backend.runComputePass('fsrRcas', {
      resources: { texSrc: srcView, texDst: target.view },
      uniforms: {
        uResolution: [width, height],
        uSharpness: this._sharpness,
        uDenoise: this._rcasDenoise ? 1 : 0,
      },
      dispatch: [width, height, 1],
    });
    return target;
  }

  getInfo() {
    const q = UPSCALE_QUALITY[this._quality];
    return {
      mode: this._mode,
      quality: this._quality,
      label: q?.label || this._quality,
      scale: q?.scale || 1,
      sharpness: this._sharpness,
      rcas: this._rcasEnabled,
      active: this.isActive(),
      // Экономия пикселей — то, ради чего апскейл вообще включают
      pixelSavings: this.isActive() ? 1 - 1 / (q.scale * q.scale) : 0,
    };
  }

  dispose() { this._disposed = true; }
}

export { COMPUTE_PASSES };
